import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { openModal } from "react-url-modal";
import { Button } from "@/components";
import { ITournament } from "@/redux/tournaments/types";
import { TournamentsItem } from "../TournamentsItem/index";
import { filterPastTournaments } from "@/utils/tournaments/tournamentHelpers";
import { groupByDate, filterLastMonths, groupByDateWithYears } from "@/utils/date/dateGrouping";
import { DateSection } from "@/components/DateSection";
import { YearSeparator } from "@/components/YearSeparator";
import { SwitcherTabBar } from "@/components/SwitcherTabBar";
import { CancelRequestModal } from "@/pages/Games/components/CancelRequestModal";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  fetchCancelTournamentApplication,
  fetchDeleteTournament,
  fetchGetMyApplicationsTournaments,
  fetchGetAvailableTournaments,
  fetchGetCreatedTournaments,
  selectTournamentsLoading,
} from "@/redux/tournaments";
import styles from "./MyTournamentsScreen.module.scss";
import { TournamentSkeleton } from "../TournamentSkeleton";
import { EmptyGameList } from "@/pages/Games/components/EmptyGameList/EmptyGameList";

interface MyTournamentsScreenProps {
  isOrganizer: boolean;
  createdTournaments: ITournament[];
  appliedTournaments: ITournament[];
  applicationsCount: number;
  setIsRequiredAuthOpened: (value: boolean) => void;
}

const SWITCHER_TABS = [
  { key: "enjoying", title: "Вы присоединились" },
  { key: "created", title: "Вы создатель" },
];

export const MyTournamentsScreen: React.FC<MyTournamentsScreenProps> = ({
  isOrganizer,
  createdTournaments,
  appliedTournaments,
  applicationsCount,
  setIsRequiredAuthOpened,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectTournamentsLoading);

  const [activeSubTab, setActiveSubTab] = useState("enjoying");
  const [cancelModalTournamentId, setCancelModalTournamentId] = useState<number | null>(null);
  const [deleteModalTournamentId, setDeleteModalTournamentId] = useState<number | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // "Участвую" — только будущие (как в мобилке EnjoyingTournamentsScreen)
  const activeApplied = useMemo(
    () => filterPastTournaments(appliedTournaments),
    [appliedTournaments]
  );
  const appliedDateGroups = useMemo(() => groupByDate(activeApplied), [activeApplied]);

  // "Созданные" — последние 6 месяцев, обратный порядок, разделители по годам
  const filteredCreated = useMemo(
    () => filterLastMonths(createdTournaments, 6),
    [createdTournaments]
  );
  const createdGrouped = useMemo(
    () => groupByDateWithYears(filteredCreated),
    [filteredCreated]
  );

  const handleCreateTournament = () => {
    openModal({ name: "createNewTournament" });
  };

  const handleCancelModalOpen = (tournamentId: number) => {
    setCancelModalTournamentId(tournamentId);
  };

  const handleCancelModalClose = () => {
    if (!isCancelling) {
      setCancelModalTournamentId(null);
    }
  };

  const handleCancelApplication = async () => {
    if (!cancelModalTournamentId) return;

    try {
      setIsCancelling(true);
      await dispatch(
        fetchCancelTournamentApplication(cancelModalTournamentId)
      ).unwrap();
      await dispatch(fetchGetMyApplicationsTournaments());
      await dispatch(fetchGetAvailableTournaments());
      setCancelModalTournamentId(null);
      openModal({ name: "successCancelTournamentRequest" });
    } catch (error) {
      console.error("Error cancelling tournament application:", error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDeleteModalOpen = (tournamentId: number) => {
    setDeleteModalTournamentId(tournamentId);
  };

  const handleDeleteModalClose = () => {
    if (!isDeleting) {
      setDeleteModalTournamentId(null);
    }
  };

  const handleDeleteTournament = async () => {
    if (!deleteModalTournamentId) return;

    try {
      setIsDeleting(true);
      await dispatch(fetchDeleteTournament(deleteModalTournamentId)).unwrap();
      await dispatch(fetchGetCreatedTournaments());
      await dispatch(fetchGetAvailableTournaments());
      setDeleteModalTournamentId(null);
      openModal({ name: "successCancelTournamentRequest" });
    } catch (error) {
      console.error("Error deleting tournament:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <SwitcherTabBar
          tabs={SWITCHER_TABS}
          activeKey={activeSubTab}
          onChange={setActiveSubTab}
        />
        <div className={styles.skeletonList}>
          {[...Array(3)].map((_, index) => (
            <TournamentSkeleton key={index} />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <SwitcherTabBar
        tabs={SWITCHER_TABS}
        activeKey={activeSubTab}
        onChange={setActiveSubTab}
      />

      {activeSubTab === "enjoying" && (
        <>
          {activeApplied.length === 0 ? (
            <EmptyGameList
              title="Ой, список ваших турниров пуст!"
              description='Чтобы добавить турнир, отправьте заявку на участие в разделе "Доступные"'
              showButton={false}
            />
          ) : (
            <div className={styles.list}>
              {appliedDateGroups.map((group) => (
                <DateSection key={group.dateLabel} dateLabel={group.dateLabel}>
                  {group.items.map((tournament) => (
                    <TournamentsItem
                      key={tournament.id}
                      item={tournament}
                      isAppliedTournament={true}
                      setIsRequiredAuthOpened={setIsRequiredAuthOpened}
                      handleCancelModalVisible={handleCancelModalOpen}
                    />
                  ))}
                </DateSection>
              ))}
            </div>
          )}
        </>
      )}

      {activeSubTab === "created" && (
        <>
          {isOrganizer ? (
            <>
              {filteredCreated.length === 0 ? (
                <EmptyGameList
                  title="Ой, список ваших турниров пуст!"
                  description="Чтобы создать турнир, нажмите кнопку ниже"
                  showButton={false}
                />
              ) : (
                <div className={styles.list}>
                  {createdGrouped.map((entry, idx) => {
                    if (entry.type === "year") {
                      return (
                        <YearSeparator key={`year-${entry.year}`} year={entry.year!} />
                      );
                    }
                    return (
                      <DateSection
                        key={`date-${entry.dateKey}-${idx}`}
                        dateLabel={entry.dateLabel!}
                      >
                        {entry.items!.map((tournament) => (
                          <TournamentsItem
                            key={tournament.id}
                            item={tournament}
                            isCreateTournaments={true}
                            setIsRequiredAuthOpened={setIsRequiredAuthOpened}
                            handleCancelModalVisible={handleDeleteModalOpen}
                            button={true}
                          />
                        ))}
                      </DateSection>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <EmptyGameList
              title="Вы ещё не организатор"
              description="Чтобы создавать турниры, подайте заявку на статус организатора"
              showButton={false}
            />
          )}
        </>
      )}

      {(activeSubTab === "enjoying" || (activeSubTab === "created" && isOrganizer)) && (
        <div className={styles.stickyButtonContainer}>
          <Button
            onClickFunc={() => {
              if (activeSubTab === "enjoying") {
                navigate("/padel/tournaments/available");
              } else {
                handleCreateTournament();
              }
            }}
            newStyles={styles.stickyButton}
          >
            {activeSubTab === "enjoying" ? "Доступные турниры" : "Создать турнир"}
          </Button>
        </div>
      )}

      <CancelRequestModal
        isOpen={cancelModalTournamentId !== null}
        setIsOpen={handleCancelModalClose}
        onConfirm={handleCancelApplication}
        isLoading={isCancelling}
      />

      <CancelRequestModal
        isOpen={deleteModalTournamentId !== null}
        setIsOpen={handleDeleteModalClose}
        onConfirm={handleDeleteTournament}
        isLoading={isDeleting}
        title="Уверены, что хотите удалить турнир?"
        confirmText="Удалить"
        loadingText="Удаление..."
      />
    </>
  );
};
